import { createRouter } from '@tanstack/react-router';

import { AppBootSplash } from '@/components/app-boot-splash';
import { logError } from '@/lib/log-error';
import { queryClient } from '@/lib/query-client';
import { routeTree } from '@/routeTree.gen';

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  // A pending `beforeLoad` (notably the cold-boot session gate on `/sign-in`
  // and `_authed`) must never leave the WebView blank — render the boot splash
  // instead of a white screen. Kept at the default `defaultPendingMs` so quick
  // in-app navigations don't flash the splash; only a genuinely stalled load
  // (>1s) surfaces it.
  defaultPendingComponent: AppBootSplash,
});

// `navigate()` almost never rejects (loader/beforeLoad errors resolve to route
// status internally — see @tanstack/router-core's `load()`), but a synchronous
// throw in `buildLocation` (bad `to`/params) or a blocker function CAN reject
// it. `useNavigate()` calls `router.navigate` fresh on every invocation
// (see @tanstack/react-router's useNavigate.tsx), so patching it here — once,
// at the single place the router is constructed — covers every call site in
// the app (~230+ of them) without touching each one individually. Any of them
// calling `navigate(...)` bare (no `await`/`.catch`) previously surfaced a
// genuine rejection as a context-free unhandled promise rejection in
// PostHog; this reports it with the attempted destination instead, then
// resolves so a caller that doesn't handle it isn't left with a floating
// rejection. A caller that DOES want to react to a failed navigation can
// still `await`/`.catch` this — it just won't reject anymore, matching what
// the two hand-written call sites that used to do this per-site
// (deep-links.ts, use-stock-order-editor.tsx) already did: log and move on.
const originalNavigate = router.navigate.bind(router);
router.navigate = ((options: Parameters<typeof originalNavigate>[0]) => {
  return originalNavigate(options).catch((error: unknown) => {
    logError('router.navigate', error, {
      feature: 'router',
      extra: { to: (options as { to?: unknown }).to },
    });
  });
}) as typeof router.navigate;

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
